import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useStyleConfig} from '@chakra-ui/react'
import {TextTitle} from '../../text-title'
import {TextTitleDescription} from '../../text-title-description'
import {remCalc} from '../../../../theme/_utils'
import {getAlignmentStyles, calculateVerticalPadding} from '../../utils'
import HeroBannerCountDown from '../../hero-banner-countdown'
import SanitizedHtml from '../../../html-sanitizer'
import {useSeoH1} from '../../../../pages/home'
import {HeroBannerTextCta} from '../hero-banner-text-cta'

export const HeroBannerText = ({
    maxWidth = remCalc(1920),
    textBlock,
    variant,
    hasPaddings,
    countdown,
    buttonText,
    isSmallHeight = false,
    isMediumHeight = false,
    isFirstElement,
}) => {
    const styles = useStyleConfig('HeroBannerText')

    const stylesBasedOnAlignment = getAlignmentStyles(textBlock?.desktopAlignment, styles)
    const desktopTextAlignment = textBlock?.desktopAlignment?.includes('center') ? 'center' : 'left'
    const verticalPadding = calculateVerticalPadding('', hasPaddings)
    const countdownEndDate = countdown?.[0]?.end_date

    const {hasSeoH1} = useSeoH1()

    return (
        <Flex
            maxW={maxWidth}
            {...stylesBasedOnAlignment.container}
            data-testid={variant}
            zIndex={2}
        >
            <Flex
                textAlign={{base: 'center', md: 'center', lg: desktopTextAlignment}}
                {...stylesBasedOnAlignment.textContent}
                py={verticalPadding}
                data-testid="hero-banner-text-flex"
            >
                {textBlock?.label && (
                    <TextTitle
                        alignment={{base: 'center', md: 'center', lg: desktopTextAlignment}}
                        data-testid="hero-banner-text-label"
                        mb={
                            isSmallHeight
                                ? remCalc(16)
                                : {
                                      base: remCalc(16),
                                      sm: remCalc(16),
                                      md: remCalc(24),
                                      lg: remCalc(24),
                                  }
                        }
                        color={
                            variant === 'black'
                                ? 'foreground.neutral.inverse'
                                : 'foreground.neutral.muted'
                        }
                        {...styles.textTitle}
                    >
                        <SanitizedHtml as={Box} html={textBlock?.label} />
                    </TextTitle>
                )}

                <TextTitleDescription
                    alignment={{base: 'center', md: 'center', lg: desktopTextAlignment}}
                    title={
                        <Box>
                            <SanitizedHtml
                                role="heading"
                                as={!hasSeoH1 && isFirstElement ? 'h1' : 'h2'}
                                html={textBlock?.title}
                            />
                        </Box>
                    }
                    titleSpacing={
                        isSmallHeight
                            ? remCalc(16)
                            : isMediumHeight
                            ? remCalc(24)
                            : {base: remCalc(16), md: remCalc(24), lg: remCalc(40)}
                    }
                    color={
                        variant === 'black'
                            ? 'foreground.neutral.inverse'
                            : 'foreground.neutral.default'
                    }
                    childrenTextColor={
                        variant === 'black'
                            ? 'foreground.neutral.inverse'
                            : 'foreground.neutral.default'
                    }
                    mb={
                        isSmallHeight
                            ? remCalc(16)
                            : isMediumHeight
                            ? remCalc(24)
                            : {base: remCalc(16), md: remCalc(24), lg: remCalc(40)}
                    }
                    noMinHeight
                    {...styles.textTitleDescription}
                >
                    <SanitizedHtml as={Box} html={textBlock?.bodyCopy} />
                </TextTitleDescription>

                {countdownEndDate && (
                    <Flex
                        justify={{
                            base: 'center',
                            lg: textBlock?.desktopAlignment?.includes('center')
                                ? 'center'
                                : 'flex-start',
                        }}
                    >
                        <HeroBannerCountDown endingDate={countdownEndDate} variant={variant} />
                    </Flex>
                )}

                <HeroBannerTextCta
                    buttonText={buttonText}
                    variant={variant}
                    desktopTextAlignment={desktopTextAlignment}
                    styles={styles}
                />
            </Flex>
        </Flex>
    )
}

HeroBannerText.propTypes = {
    textBlock: PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        bodyCopy: PropTypes.string,
        desktopAlignment: PropTypes.string,
        tabletMobileAlignment: PropTypes.string,
    }),
    buttonText: PropTypes.object,
    maxWidth: PropTypes.string,
    variant: PropTypes.string,
    hasPaddings: PropTypes.bool,
    countdown: PropTypes.array,
    isSmallHeight: PropTypes.bool,
    isMediumHeight: PropTypes.bool,
    isFirstElement: PropTypes.bool,
}
